(ns app.front.view.application.utils
  (:require [app.domain.application.shared-process :as sp]
            [app.front.process :as process]
            [core.util.util :as util]
            [tilakone.core :as tk]
            [tilakone.util :as tu]))


(defn- process-state-positions
  "Loops through states (vector param), and associates states with :my-position key.
   Value of key is:
     :before  - if state is 'before' current state
     :current - if state is current state
     :after   - if state is after current state."
  [current-state states]
  (->> states
       (reduce (fn [acc state]
                 (let [this (if (= :current (:latest-status acc))
                              :after
                              (if (= (:domain/state state) current-state)
                                :current
                                (:latest-status acc)))]
                   (-> acc
                       (assoc :latest-status this)
                       (update :states conj (assoc state :my-position this)))))
               {:latest-status :before
                :states        []})
       :states))


(defn- check-guard? [process guard]
  ((:tilakone.core/guard? process) (assoc process :tilakone.core/guard guard)))

(defn phase-step-state
  "Returns a map containing information about the current and next
   step in the process. The result looks like:

  {:current-step  <e.g. Permit Issued>
   :current-index 6
   :steps-count   7
   :next-step     <e.g. Permit Issued>}"
  [state entity-process]
  (if state
    (let [positions      (process-state-positions state
                                                  (->> entity-process
                                                       ::tk/states
                                                       (filter :domain/state)
                                                       (util/distinct-by :domain/state)))
          current-track  (->> positions
                              (filter #(= :current (:my-position %)))
                              first
                              :track)
          positions      (->> positions
                              (filter (fn [{:keys [track next-step-guards]}]
                                        (and (= current-track track)
                                             (every? #(check-guard? entity-process %) next-step-guards)))))
          complete-count (->> positions
                              (filter #(= :before (:my-position %)))
                              count)]
      {:current-step  state
       :current-index (+ complete-count 1)
       :steps-count   (count positions)
       :next-step     (->> positions
                           (filter (fn [{:keys [my-position next-step-guards]}]
                                     (and (= :after my-position)
                                          (every? #(check-guard? entity-process %) next-step-guards))))
                           first
                           :domain/state)})
    {}))

(defn direct-transitions
  [state process]
  (let [process-state->entity-state (->> process
                                         ::tk/states
                                         (map (juxt ::tk/name :domain/state))
                                         (into {}))]
    (->> process
         ::tk/states
         (filter (comp (partial = state)
                       :domain/state))
         first
         ::tk/transitions
         (filter #(-> % :signal-type (= :direct)))
         (filter (fn [{::tk/keys [on]}]
                   (process/signal-permitted? process [on])))
         (map (fn [{process-state ::tk/to :as transition}]
                (assoc transition
                       :to-domain-state
                       (process-state->entity-state process-state)))))))


(defn visible-primary-actions
  "The subset of `actions` that the UI offers as primary buttons: keep all
  `:ui-type :primary` candidates, but when there is more than one drop the disabled
  ones so a clearly-available action is not obscured by a sibling that cannot yet be
  taken. This mirrors the rule `primary-actions-buttons-expanded` applies when
  rendering the workspace sub-header, and is used by the application wizard to pick
  the single primary action for its summary/payment step."
  [actions]
  (let [primaries (filter #(= :primary (:ui-type %)) actions)]
    (if (next primaries)
      (remove #(= :disabled (:status %)) primaries)
      primaries)))


(defn resolve-type
  "Resolve :secondary actions as :select, e.g. when rendering the actions in mobile."
  [standalone-secondary? type-def]
  (let [ui-type (get type-def :type type-def)]
    (if (and (= ui-type :secondary)
             (not standalone-secondary?))
      :select
      ui-type)))

(defn resolve-primary-actions
  "There are two kinds of primary actions in the state machine:

  Primary signals that are denoted by `:signal-type`

  Non-signal (not triggering a command) UI primary actions are denoted by `:primary-action` key, value of
  which is a map:

  `:type`: Same as signal-type (`:select`, `:primary`, `:secondary`)
  `:info`: (optional) L10n key for information message. The messages are shown only for buttons."
  [process standalone-secondary?]
  (let [status-fn (fn [pairs k]
                    (for [[signal-id x] pairs
                          :let          [[status _] (sp/signal-allowed-status process signal-id)
                                         ui-type (resolve-type standalone-secondary? x)]
                          :when         (and (not= status :forbidden)
                                             (or (not= ui-type :select) (not= status :disabled)))]
                      {:signal-id signal-id
                       k          x
                       :ui-type   ui-type
                       :confirm?  (try
                                    (:confirm? (tu/get-transition process [signal-id]))
                                    (catch js/Error _
                                      false))
                       :status    status
                       :errors    (sp/visible-errors-for-signal process signal-id)}))
        signals   (status-fn (sp/primary-signals process) :signal-type)
        others    (status-fn (sp/filter-signals-by-key process :primary-action) :primary-action)]
    (concat signals others)))
